import React, { useState, useEffect } from 'react';
import { uniq } from 'lodash';
import cc from 'classcat';
import { faCopy, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Classes, FormGroup } from '@blueprintjs/core';
import {
  MultiSelect,
  ItemRenderer,
  ItemPredicate,
  ItemListRenderer
} from '@blueprintjs/select';
import {
  Form,
  FormInstance,
  FormErrors,
  TextArea,
  useField,
  Markup,
  MarkupProps,
  SelectBooleanInput
} from 'blueprint-form';

import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import AutoText from '../../atoms/text/auto';
import Button from '../../molecules/button';
import Drawer from '../../molecules/drawer';
import Card, { CardBlock } from '../card';
import Pagination from '../pagination';
import Modal from '../modal';
import Toast from '../toast';
import Callout from '../callout';
import { Menu, MenuItem } from '../menu';
import FormSelectApp from '../select/form/apps';
import {
  IPMTemplateTag,
  useGetPMTemplateTags,
  CreatePMTemplate,
  IPMTemplate,
  RemovePMTemplate
} from '../../../libs/pm';
import { useApps } from '../../../contexts/apps';
import { useTerms } from '../../../contexts/terms';
import { useAuth } from '../../../contexts/auth';
import { PMTemplateCardMeta } from './card';

type TagItem = Partial<IPMTemplateTag> & {
  value: string;
};

interface TagsInputProps extends MarkupProps {
  placeholder?: string;
  maxItems?: number;
  minItems?: number;
  allowCreate?: boolean;
}

const TagMultiSelect = MultiSelect.ofType<TagItem>();

const GetTagItemsFromTagsValue = (value: string): TagItem[] => {
  const tags: TagItem[] = [];
  if (value) {
    const parts = uniq(value.split(','));
    for (const part of parts) {
      tags.push({
        value: part
      });
    }
  }
  return tags;
};

export const PMTemplateTagsInput = ({
  label,
  name,
  placeholder,
  fill,
  large,
  disabled,
  minItems,
  maxItems,
  allowCreate = true
}: TagsInputProps): JSX.Element => {
  // Setup the field
  const id = Markup.getID();
  const [field, meta, helpers] = useField<string>({
    name: name,
    validate: (value: string) => {
      let error: string = null;
      if (minItems || maxItems) {
        const items = value.split(',').filter((item) => item !== '');
        if (items.length < minItems) {
          if (minItems === 1) {
            error = 'At least 1 tag is required!';
          } else {
            error = `At least ${minItems} tags are required!`;
          }
        }
        if (items.length > maxItems) {
          if (maxItems === 1) {
            error = 'Only 1 tag can be selected!';
          } else {
            error = `Only ${maxItems} tags can be selected!`;
          }
        }
      }
      return error;
    }
  });
  const initialTags: TagItem[] = GetTagItemsFromTagsValue(field.value);
  const [tagsSelected, setTagsSelected] = useState<TagItem[]>(initialTags);
  const { tags } = useGetPMTemplateTags({});
  const isTagEqual = (a: TagItem, b: TagItem): boolean => {
    return a.value.trim() === b.value.trim();
  };
  const isTagInArray = (tag: TagItem, tags: TagItem[]): boolean => {
    return tags.some((_tag) => isTagEqual(tag, _tag));
  };
  const filterTag: ItemPredicate<TagItem> = (
    query,
    tag,
    _index,
    exactMatch
  ) => {
    const normalizedValue = tag.value.trim().toLowerCase();
    const normalizedQuery = query.trim().toLowerCase();
    if (exactMatch) {
      return normalizedQuery === normalizedValue;
    }
    return normalizedValue.includes(normalizedQuery);
  };
  const selectTag = (tag: TagItem) => {
    if (isTagInArray(tag, tagsSelected)) {
      return;
    }
    const tags = [...tagsSelected, tag];
    setTagsSelected(tags);
    helpers.setTouched(true);
    helpers.setValue(tags.map((t) => t.value.trim()).join(','));
  };
  const deselectTag = (value: string) => {
    const tags = tagsSelected.filter(
      (tag) => tag.value.trim() !== value.trim()
    );
    setTagsSelected(tags);
    helpers.setTouched(true);
    helpers.setValue(tags.map((t) => t.value.trim()).join(','));
  };
  const addNewTag = (query: string) => {
    const value = query
      .trim()
      .replace(/[^a-zA-Z0-9- ]/g, '')
      .trim();
    const tag: TagItem = { value };
    const tags = [...tagsSelected, tag];
    setTagsSelected(tags);
    helpers.setValue(tags.map((t) => t.value.trim()).join(','));
    return;
  };
  const renderTags: ItemListRenderer<TagItem> = ({
    items,
    itemsParentRef,
    query,
    renderItem
  }) => {
    const renderedItems = items.map(renderItem).filter((item) => item != null);
    return (
      <Menu
        ulRef={itemsParentRef}
        style={{
          maxHeight: '340px',
          overflowY: 'auto'
        }}
      >
        {renderedItems.length === 0 && !query && (
          <li>
            <span className="bp5-menu-item">
              <span className="bp5-text-overflow-ellipsis bp5-fill">
                No tags left.
              </span>
            </span>
          </li>
        )}
        {query && (
          <MenuItem
            disabled={true}
            text={`Found ${renderedItems.length} tags matching "${query}".`}
          />
        )}
        {renderedItems}
        {query && allowCreate && (
          <MenuItem
            icon="add"
            text={`Create Tag: "${query}"`}
            onClick={() => addNewTag(query)}
            shouldDismissPopover={false}
          />
        )}
      </Menu>
    );
  };
  const renderTagOption: ItemRenderer<TagItem> = (
    tag,
    { modifiers, handleClick }
  ) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    if (isTagInArray(tag, tagsSelected)) {
      return null;
    }
    return (
      <MenuItem
        key={tag.id}
        text={tag.value}
        shouldDismissPopover={false}
        onClick={handleClick}
      />
    );
  };
  // Update the actual field on selection change
  useEffect(() => {
    helpers.setValue(tagsSelected.map((tag) => tag.value.trim()).join(','));
  }, [tagsSelected]);
  useEffect(() => {
    const tags = GetTagItemsFromTagsValue(field.value);
    setTagsSelected(tags);
  }, [field.value]);
  return (
    <FormGroup
      label={label}
      intent={meta.touched && meta.error ? 'danger' : 'none'}
      helperText={meta.touched ? meta.error : null}
    >
      <TagMultiSelect
        tagInputProps={{
          intent: meta.touched && meta.error ? 'danger' : 'none',
          inputProps: {
            'aria-label': label,
            autoComplete: 'off'
          },
          onRemove: deselectTag,
          large: large,
          disabled: disabled,
          placeholder: placeholder
        }}
        popoverProps={{
          position: 'auto',
          portalClassName: 'z-100'
        }}
        itemListRenderer={renderTags}
        itemRenderer={renderTagOption}
        itemPredicate={filterTag}
        tagRenderer={(tag) => tag.value}
        items={tags}
        selectedItems={tagsSelected}
        itemsEqual={isTagEqual}
        onItemSelect={selectTag}
        fill={fill}
        resetOnSelect
      />
      <input id={id} type="hidden" name={name} value={field.value} />
    </FormGroup>
  );
};

interface AddPMTemplateProps {
  refetch: (isReset?: boolean) => void;
  btnText?: string;
  className?: string;
  templateToClone: IPMTemplate | null;
  setTemplateToClone: (template: IPMTemplate | null) => void;
}

interface PMTemplateFormData {
  app?: string;
  body: string[];
  tags?: string;
  isChatTemplate?: boolean;
}

// eslint-disable-next-line no-control-regex
const REGEX_TEMPLATE = /^[\x00-\x7F]{1,160}$/;
const REGEX_VARIABLE_TEMPLATE =
  /\{{ FIRST_NAME \}}|\{{FIRST_NAME\}}|\{{ LAST_NAME \}}|\{{LAST_NAME\}}/i;

export const AddPMTemplate: React.FC<AddPMTemplateProps> = ({
  refetch,
  btnText,
  className,
  templateToClone,
  setTemplateToClone
}) => {
  const { terms } = useTerms();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * We have to add individual pieces of state
   * for the different form fields, so that
   * the dialog properly displays cloned template
   * values when it opens.
   */
  const [body, setBody] = useState(['']);
  const [app, setApp] = useState('');
  const [tags, setTags] = useState('');
  const [isChatTemplate, setIsChatTemplate] = useState(false);
  const templateId = templateToClone !== null ? templateToClone.id : null;
  const form = new FormInstance<PMTemplateFormData>({
    app: app,
    body: body,
    tags: tags,
    isChatTemplate: isChatTemplate
  });
  const onChange = (data: PMTemplateFormData) => {
    setBody(data.body);
    setApp(data.app);
    setTags(data.tags);
    setIsChatTemplate(data.isChatTemplate);
  };

  const toggleAddOpen = () => {
    if (isAddOpen) {
      setIsAddOpen(false);
      setTemplateToClone(null);
      setBody(['']);
      setApp('');
      setTags('');
      setIsChatTemplate(false);
      form.getForm().resetForm({
        values: {
          app: '',
          body: [''],
          tags: '',
          isChatTemplate: false
        }
      });
      refetch(true);
    } else {
      setIsAddOpen(true);
    }
  };

  const validate = (values: PMTemplateFormData): FormErrors => {
    const errors: FormErrors = {};
    for (const [i, segment] of values.body.entries()) {
      let error: string = null;
      if (segment.trim().length > 160) {
        error = 'The message body can be max 160 characters!';
      } else if (segment.trim() === '') {
        error = 'The message body cannot be empty!';
      } else if (!REGEX_TEMPLATE.test(segment.trim())) {
        error = 'The content contains illegal characters!';
      } else if (
        !isChatTemplate &&
        REGEX_VARIABLE_TEMPLATE.test(segment.trim())
      ) {
        error =
          'Auto populating patient variables are only allowed for chat templates!';
      }
      if (error) {
        if (!errors.body) {
          errors.body = [];
        }
        errors.body[i] = error;
      }
    }
    return errors;
  };

  const onSubmit = (data: PMTemplateFormData) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    CreatePMTemplate(data).then((response) => {
      if (response.status !== 'OK') {
        setIsLoading(false);
        Modal.error({
          title: 'Error Saving Template',
          content: response.message
        });
        return;
      }
      refetch(true);
      setIsLoading(false);
      setIsAddOpen(false);
      form.getForm().resetForm({
        values: {
          app: '',
          body: [''],
          tags: '',
          isChatTemplate: false
        }
      });
      Toast({
        intent: 'success',
        message: 'The template has been saved.'
      });
    });
  };

  const onAddSegment = () => {
    if (body.length >= 3) {
      return;
    }
    form.setData({
      body: [...body, ''],
      app: app,
      tags: tags,
      isChatTemplate: isChatTemplate
    });
  };

  const onRemoveSegment = () => {
    if (body.length <= 1) {
      return;
    }
    form.setData({
      body: body.slice(0, body.length - 1),
      app: app,
      tags: tags,
      isChatTemplate: isChatTemplate
    });
  };

  useEffect(() => {
    if (templateId !== null) {
      setIsAddOpen(true);
      setBody(templateToClone.body);
      setTags(templateToClone.tags.join(','));
      setApp(templateToClone.app);
      setIsChatTemplate(templateToClone.isChatTemplate);
      form.setData({
        app: templateToClone.app,
        body: templateToClone.body,
        tags: templateToClone.tags.join(','),
        isChatTemplate: templateToClone.isChatTemplate
      });
    }
  }, [templateId]);

  return (
    <React.Fragment>
      <Drawer
        isOpen={isAddOpen}
        onClose={toggleAddOpen}
        title={
          <span>
            <Icon className="mr-4" icon={faPlus} />
            Add Message Template
          </span>
        }
      >
        <div className={Classes.DRAWER_BODY}>
          <CardBlock>
            <Form
              form={form}
              onSubmit={onSubmit}
              onChange={onChange}
              validate={validate}
            >
              {(props) => {
                return (
                  <>
                    {props.values.body.map((segment, i) => (
                      <TextArea
                        key={i}
                        label={`Message Segment #${i + 1}`}
                        name={`body[${i}]`}
                        style={{
                          height: '150px'
                        }}
                        fill
                        placeholder="e.g. Please take your medications as instructed."
                      />
                    ))}
                    <div className="mb-4">
                      {props.values.body.length < 3 && (
                        <Button
                          intent="primary"
                          size="small"
                          bare
                          variant="minimal"
                          onClick={onAddSegment}
                        >
                          Add Another
                        </Button>
                      )}
                      {props.values.body.length > 1 && (
                        <Button
                          intent="danger"
                          size="small"
                          bare
                          variant="minimal"
                          onClick={onRemoveSegment}
                        >
                          Remove One
                        </Button>
                      )}
                    </div>
                    <Callout intent="primary" className="mb-6">
                      Message can only contain standard ASCII characters, to the
                      max of 160 characters. White spaces before and after the
                      message body will be trimmed automatically.
                    </Callout>
                    <PMTemplateTagsInput
                      label="Tags (Optional)"
                      name="tags"
                      maxItems={3}
                      fill
                    />
                    <FormSelectApp label="Program (Optional)" name="app" />
                    <SelectBooleanInput
                      label="Use as chat template"
                      name="isChatTemplate"
                      trueLabel="Yes"
                      falseLabel="No"
                    />
                    <Callout intent="primary" className="mb-6">
                      Picking <b>Yes</b> will make this template a chat
                      template, to be used in a {terms.patient.default}'s chat
                      for faster communications. Including{' '}
                      <b>{'{{ FIRST_NAME }}'}</b> and/or{' '}
                      <b>{'{{ LAST_NAME }}'}</b> in your message will auto
                      populate the {terms.patient.default}'s first and last
                      name.
                    </Callout>
                    <Button
                      className="mt-8"
                      type="submit"
                      intent="primary"
                      icon={<Icon icon={faPlus} />}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving Template...' : 'Save Template'}
                    </Button>
                  </>
                );
              }}
            </Form>
          </CardBlock>
        </div>
      </Drawer>
      <Button intent="primary" className={className} onClick={toggleAddOpen}>
        {btnText ? btnText : 'Add Template'}
      </Button>
    </React.Fragment>
  );
};

interface PMTemplateProps {
  template: IPMTemplate;
  onRemove: (template: IPMTemplate) => void;
  onClone: (template: IPMTemplate) => void;
  isLoading?: boolean;
}

const PMTemplate = ({
  template,
  onRemove,
  onClone,
  isLoading
}: PMTemplateProps): JSX.Element => {
  const { apps: appConfigs } = useApps().data || {};
  const classes = cc(['mb-4']);
  return (
    <Card className={classes}>
      <Row>
        <Col sm={16}>
          {template.body.map((segment, i) => (
            <AutoText key={i} className="ma-0">
              {segment}
            </AutoText>
          ))}
          <PMTemplateCardMeta template={template} apps={appConfigs} />
        </Col>
        <Col sm={8} className="text-right">
          <Button
            intent="primary"
            size="small"
            className="mr-2"
            onClick={() => onClone(template)}
            loading={isLoading}
          >
            <Icon icon={faCopy} /> Clone
          </Button>
          <Button
            intent="danger"
            size="small"
            onClick={() => onRemove(template)}
            loading={isLoading}
          >
            <Icon icon={faTrash} /> Remove
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

interface PMTemplateListProps {
  templates: IPMTemplate[];
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setTemplateToClone: (template: IPMTemplate) => void;
  refetch: (isReset?: boolean) => void;
}

const PMTemplateList = ({
  templates,
  total,
  page,
  setPage,
  setTemplateToClone,
  limit,
  refetch
}: PMTemplateListProps): JSX.Element => {
  const [isRemoving, setIsRemoving] = useState(false);
  const { user, updateUser } = useAuth();
  const onRemove = (template: IPMTemplate) => {
    if (isRemoving) {
      return;
    }
    Modal.confirm({
      title: 'Remove Template',
      content: 'Are you sure you want to remove this template?',
      onOk: () => {
        setIsRemoving(true);
        RemovePMTemplate(template.id).then((response) => {
          setIsRemoving(false);
          if (response.status !== 'OK') {
            Modal.error({
              title: 'Error Removing Template',
              content: response.message
            });
            return;
          }
          // Update local user state to remove deleted template from pinned list
          const pinnedIds: number[] = user?.configs?.pinnedTemplateIds || [];
          if (pinnedIds.includes(template.id)) {
            updateUser({
              ...user,
              configs: {
                ...user.configs,
                pinnedTemplateIds: pinnedIds.filter((id) => id !== template.id)
              }
            });
          }
          Toast({
            intent: 'success',
            message: 'The template has been removed.'
          });
          refetch(true);
        });
      }
    });
  };
  const onClone = (template: IPMTemplate) => {
    setTemplateToClone(template);
  };
  return (
    <React.Fragment>
      <div>
        {templates.map((template) => (
          <PMTemplate
            key={template.id}
            template={template}
            onRemove={onRemove}
            onClone={onClone}
          />
        ))}
      </div>
      <div className="mt-6">
        <Pagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
        />
      </div>
    </React.Fragment>
  );
};

export default PMTemplateList;
